/* GrowthOS — Call modal for the lead record (softphone overlay).
   Ported pixel-for-pixel from design growthos/Modals.CallModal: Calling… → Connected
   with a live timer, mute toggle, end-call + close, lead detail rows, and a
   "Fill Disposition" action. This is a simulated call surface (the app has no
   click-to-call telephony backend); it shows the lead's real contact details and
   the End / Close / Fill Disposition actions dismiss it. */
import { useEffect, useState } from 'react';

const ICONS = {
  grid: <path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z" />,
  phone: <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3-8.6A2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />,
  mic: (<><path d="M12 15a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3" /></>),
  micOff: <path d="M9 9v3a3 3 0 0 0 5.1 2.1M15 9.3V6a3 3 0 0 0-5.9-.7M19 10v2a7 7 0 0 1-.6 2.8M5 10v2a7 7 0 0 0 10.7 6M12 19v3M2 2l20 20" />,
  x: <path d="M18 6 6 18M6 6l12 12" />,
  user: (<><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>),
  mail: <path d="M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2ZM22 7l-10 6L2 7" />,
  location: (<><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="2.5" /></>),
  globe: (<><circle cx="12" cy="12" r="9" /><path d="M2.5 12h19M12 3a15 15 0 0 1 0 18M12 3a15 15 0 0 0 0 18" /></>),
  edit: <path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />,
  arrowUpRight: <path d="M7 17 17 7M7 7h10v10" />,
};
const Ic = ({ name, size = 16, className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
    {ICONS[name] || null}
  </svg>
);

const AVA_COLORS = ['#c96342', '#2e63a6', '#2c7a45', '#9a751c', '#6a4fae', '#b34480', '#54738f'];
const initials = (name) =>
  String(name || '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0] || '')
    .join('')
    .toUpperCase() || '?';
const avaColor = (name) =>
  AVA_COLORS[
    String(name || '')
      .split('')
      .reduce((a, c) => a + c.charCodeAt(0), 0) % AVA_COLORS.length
  ];

const LeadCallModal = ({ lead = {}, onClose }) => {
  const [phase, setPhase] = useState('calling'); // calling -> connected
  const [secs, setSecs] = useState(0);
  const [muted, setMuted] = useState(false);

  const fullName = lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Lead';

  useEffect(() => {
    const t = setTimeout(() => setPhase('connected'), 2600);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    if (phase !== 'connected') return undefined;
    const i = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(i);
  }, [phase]);

  const connected = phase === 'connected';
  const mm = String(Math.floor(secs / 60)).padStart(2, '0');
  const ss = String(secs % 60).padStart(2, '0');

  const rows = [
    ['user', 'Name', fullName],
    ['mail', 'Email', lead.email || '—'],
    ['phone', 'Phone', lead.phone || lead.mobile || '—'],
    ['location', 'Region', lead.state || '—'],
    ['globe', 'Timezone', lead.timeZone || '—'],
  ];

  return (
    <div className="gos-import-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className={'gos-call-modal' + (connected ? ' connected' : '')} role="dialog" aria-modal="true" aria-label="Call">
        {/* call bar */}
        <div className="gos-call-bar">
          <Ic name="grid" size={18} className="gos-call-grid" />
          <Ic name="phone" size={17} className={connected ? 'gos-call-ph-on' : 'gos-call-ph'} />
          <span className={'gos-call-status' + (connected ? ' on' : '')}>
            {connected ? 'Connected' : 'Calling…'}
          </span>
          <div className="gos-call-spacer" />
          <button type="button" className={'gos-call-round' + (muted ? ' muted-on' : '')} onClick={() => setMuted((m) => !m)} title={muted ? 'Unmute' : 'Mute'}>
            <Ic name={muted ? 'micOff' : 'mic'} size={16} />
          </button>
          <span className="gos-call-timer">{connected ? `${mm}:${ss}` : '00:00'}</span>
          <button type="button" className="gos-call-end" onClick={onClose} title="End call">
            <Ic name="phone" size={17} style={{ transform: 'rotate(135deg)' }} />
          </button>
          <button type="button" className="gos-lr-iconbtn gos-lr-iconbtn-sm" onClick={onClose} aria-label="Close">
            <Ic name="x" size={17} />
          </button>
        </div>

        {/* lead header card */}
        <div className="gos-call-card gos-call-lead">
          <span className="gos-call-ava" style={{ background: avaColor(fullName) }}>{initials(fullName)}</span>
          <div className="gos-call-lead-meta">
            <div className="gos-call-name">{fullName}</div>
            {lead.companyName ? <div className="gos-call-company">{lead.companyName}</div> : null}
          </div>
        </div>

        {/* detail rows */}
        <div className="gos-call-card gos-call-rows">
          {rows.map((r, i) => (
            <div className="gos-lr-drow" key={i}>
              <div className="gos-lr-dlabel">
                <Ic name={r[0]} size={15} /> {r[1]}
              </div>
              <div className={'gos-lr-dval' + (r[2] === '—' ? ' muted' : '')}>{r[2]}</div>
            </div>
          ))}
        </div>

        {/* disposition */}
        <div className="gos-call-foot">
          <button type="button" className="gos-call-dispo" onClick={onClose}>
            <Ic name="edit" size={16} /> Fill Disposition
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadCallModal;
